import type { PaymentIntentDataModel } from '../interfaces/paymentIntent.interface';
import type { PaymentIntentCacheEntry, PaymentIntentKind } from '../types/paymentInit';
import { isPaymentIntentUsable } from '../utils/paymentIntent';

const cache = new Map<string, PaymentIntentCacheEntry>();

function isCurrentRequest(key: string, request: Promise<PaymentIntentDataModel>): boolean {
  const cached = cache.get(key);

  return cached?.status === 'pending' && cached.request === request;
}

export function usePaymentIntentCache(kind: PaymentIntentKind) {
  function keyFor(amount: number): string {
    return `${kind}:${amount}`;
  }

  async function ensure(
    amount: number,
    createIntent: () => Promise<PaymentIntentDataModel>,
  ): Promise<PaymentIntentDataModel> {
    const key = keyFor(amount);
    const cached = cache.get(key);

    if (cached?.status === 'pending') return cached.request;

    if (cached?.status === 'ready') {
      const isReusable = isPaymentIntentUsable(
        { amount: cached.amount, expiresAt: cached.intent.expires_at, fetchedAtMs: cached.fetchedAtMs },
        amount,
        Date.now(),
      );

      if (isReusable) return cached.intent;
    }

    const request: Promise<PaymentIntentDataModel> = createIntent()
      .then((intent) => {
        if (isCurrentRequest(key, request)) cache.set(key, { status: 'ready', amount, intent, fetchedAtMs: Date.now() });

        return intent;
      })
      .catch((error) => {
        if (isCurrentRequest(key, request)) cache.delete(key);

        throw error;
      });

    cache.set(key, { status: 'pending', request });

    return request;
  }

  function drop(amount: number): void {
    cache.delete(keyFor(amount));
  }

  return { ensure, drop };
}
